import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import Select from 'react-select';
import {
  createOrUpdateFeature,
  Feature,
  FeatureCondition
} from '../../../api/featureManagement/featureService';
import {
  COMPARATOR_OPTIONS,
  buildConditionValue,
  displayConditionValue,
} from '../conditionUtils';

interface FeatureFormModalProps {
  show: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  feature?: Feature | null;
  clientId?: string;
  scope?: string;
}


const STATUS_OPTIONS = [
  { value: 'ENABLED', label: 'Enabled' },
  { value: 'DISABLED', label: 'Disabled' },
  { value: 'KILLED', label: 'Killed' },
];

const FeatureFormModal: React.FC<FeatureFormModalProps> = ({
  show,
  handleClose,
  onSuccess,
  feature,
  clientId: propClientId,
  scope: propScope
}) => {
  const [formData, setFormData] = useState<{
    featureName: string;
    scope: string;
    status: 'ENABLED' | 'DISABLED' | 'KILLED';
    clientId: string;
    conditions: FeatureCondition[];
    setConditions: boolean;
  }>({
    featureName: '',
    scope: propScope || 'tournament',
    status: 'ENABLED' as 'ENABLED' | 'DISABLED' | 'KILLED',
    clientId: '',
    conditions: [] as FeatureCondition[],
    setConditions: false
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      if (feature) {
        setFormData({
          featureName: feature.name,
          scope: feature.scope,
          status: feature.status,
          clientId: feature.clientId,
          conditions: (feature.conditions || []).map((c) => ({
            name: c.name,
            comparator: String(c.comparator ?? 'EQ').toUpperCase(),
            value: displayConditionValue(c.value),
          })),
          setConditions: !!(feature.conditions && feature.conditions.length > 0)
        });
      } else {
        setFormData({
          featureName: '',
          scope: propScope || 'tournament',
          status: 'ENABLED',
          clientId: '',
          conditions: [],
          setConditions: false
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [show, feature, propScope]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.featureName.trim()) {
      newErrors.featureName = 'Feature Name is required';
    }

    if (!formData.scope) {
      newErrors.scope = 'Scope is required';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Client ID is required';
    }

    if (formData.setConditions) {
      formData.conditions.forEach((cond, index) => {
        if (!cond.name.trim()) {
          newErrors[`condition_${index}_name`] = 'Condition name is required';
        }
        if (!cond.comparator) {
          newErrors[`condition_${index}_comparator`] = 'Comparator is required';
        }
        if (cond.value === undefined || cond.value === null || String(cond.value).trim() === '') {
          newErrors[`condition_${index}_value`] = 'Value is required';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const payload = {
        name: formData.featureName.trim(),
        scope: formData.scope,
        status: formData.status,
        clientId: formData.clientId,
        conditions: formData.setConditions
          ? formData.conditions.map((cond) => ({
              name: cond.name.trim(),
              comparator: cond.comparator,
              value: buildConditionValue(cond.comparator, String(cond.value)),
            }))
          : undefined,
        setConditions: formData.setConditions
      };

      await createOrUpdateFeature(payload);
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error saving feature:', err);
      setSubmitError(err?.response?.data?.message || 'Failed to save feature. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { name: '', comparator: 'EQ', value: '' }],
      setConditions: true
    }));
  };

  const handleRemoveCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
      setConditions: prev.conditions.length > 1
    }));
  };

  const handleConditionChange = (index: number, field: keyof FeatureCondition, value: any) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((cond, i) =>
        i === index ? { ...cond, [field]: value } : cond
      )
    }));
    if (errors[`condition_${index}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`condition_${index}_${field}`];
        return newErrors;
      });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{feature ? 'Edit Feature' : 'Create New Feature'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {submitError && <Alert variant="danger">{submitError}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>
              Feature Name<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.featureName}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, featureName: e.target.value }));
                if (errors.featureName) setErrors(prev => ({ ...prev, featureName: '' }));
              }}
              isInvalid={!!errors.featureName}
              placeholder="e.g., Live Streaming"
              disabled={!!feature}
              style={feature ? { pointerEvents: 'none' } : undefined}
            />
            {errors.featureName && <Form.Control.Feedback type="invalid">{errors.featureName}</Form.Control.Feedback>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Scope<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.scope}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, scope: e.target.value }));
                if (errors.scope) setErrors(prev => ({ ...prev, scope: '' }));
              }}
              isInvalid={!!errors.scope}
              placeholder="e.g., tournament"
              disabled={!!feature}
              style={feature ? { pointerEvents: 'none' } : undefined}
            />
            {errors.scope && <Form.Control.Feedback type="invalid">{errors.scope}</Form.Control.Feedback>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Status<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Select
              classNamePrefix="react-select"
              value={STATUS_OPTIONS.find(opt => opt.value === formData.status)}
              onChange={(option) => {
                setFormData(prev => ({ ...prev, status: option?.value as any || 'ENABLED' }));
                if (errors.status) setErrors(prev => ({ ...prev, status: '' }));
              }}
              options={STATUS_OPTIONS}
              isClearable={false}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: errors.status ? '#dc3545' : state.isFocused ? '#80bdff' : '#ced4da',
                  '&:hover': {
                    borderColor: errors.status ? '#dc3545' : state.isFocused ? '#80bdff' : '#ced4da'
                  }
                }),
                singleValue: (base) => ({
                  ...base,
                  color: formData.status === 'ENABLED' ? '#1B9E5C' : base.color
                })
              }}
            />
            {errors.status && <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.status}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Client ID<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.clientId}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, clientId: e.target.value }));
                if (errors.clientId) setErrors(prev => ({ ...prev, clientId: '' }));
              }}
              isInvalid={!!errors.clientId}
              placeholder="e.g., client_id"
              disabled={!!feature}
              style={feature ? { pointerEvents: 'none' } : undefined}
            />
            {errors.clientId && <Form.Control.Feedback type="invalid">{errors.clientId}</Form.Control.Feedback>}
          </Form.Group>

          {/* Conditions Section */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0 fw-bold">Conditions</Form.Label>
              <Button
                variant="outline-primary"
                size="sm"
                type="button"
                onClick={handleAddCondition}
              >
                + Add Condition
              </Button>
            </div>

            {formData.conditions.length === 0 ? (
              <div className="text-muted" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                No conditions added. Click "Add Condition" to add one.
              </div>
            ) : (
              formData.conditions.map((condition, index) => (
                <div key={index} className="mb-3">
                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.875rem' }}>Condition Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={condition.name}
                          onChange={(e) => handleConditionChange(index, 'name', e.target.value)}
                          isInvalid={!!errors[`condition_${index}_name`]}
                          placeholder="e.g., purchase_count"
                        />
                        {errors[`condition_${index}_name`] && (
                          <Form.Control.Feedback type="invalid">{errors[`condition_${index}_name`]}</Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.875rem' }}>Comparator</Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          value={COMPARATOR_OPTIONS.find(opt => opt.value === condition.comparator) || null}
                          onChange={(option) => handleConditionChange(index, 'comparator', option?.value || 'EQ')}
                          options={COMPARATOR_OPTIONS}
                          isClearable={false}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: errors[`condition_${index}_comparator`] ? '#dc3545' : base.borderColor,
                            }),
                          }}
                        />
                        {errors[`condition_${index}_comparator`] && (
                          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {errors[`condition_${index}_comparator`]}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.875rem' }}>Value</Form.Label>
                        <Form.Control
                          type="text"
                          value={condition.value}
                          onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                          isInvalid={!!errors[`condition_${index}_value`]}
                          placeholder={
                            ['IN', 'NOTIN'].includes(condition.comparator)
                              ? 'e.g., gold, platinum'
                              : 'e.g., 5'
                          }
                        />
                        {errors[`condition_${index}_value`] && (
                          <Form.Control.Feedback type="invalid">{errors[`condition_${index}_value`]}</Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="text-end">
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      onClick={() => handleRemoveCondition(index)}
                      className="text-danger p-0"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : feature ? 'Update Feature' : 'Create Feature'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FeatureFormModal;
